<template>
  <div class="course-landing" :class="[course.slug, { banner }]">
    <div v-if="banner && $route.path === '/learn-machine-learning-process-a-z/'" class="banner-holder">
      <div class="close-banner" @click="banner = false">
        <CloseIcon />
      </div>
      <div class="banner container">
        <div class="header-wrap">
          <p class="banner-header h5">
            Limited Offer
          </p>
          <p class="banner-info">
            Get early bird access now and save {{ course.discountPercent }}%!
          </p>
        </div>
        <div class="counter">
          <div class="square days">
            <span>{{ countdown.days }}</span><span>Days</span>
          </div>
          <div class="square hours">
            <span>{{ countdown.hours }}</span><span>Hours</span>
          </div>
          <div class="square min">
            <span>{{ countdown.minutes }}</span><span>Minutes</span>
          </div>
          <div class="square sec">
            <span>{{ countdown.seconds }}</span><span>Seconds</span>
          </div>
        </div>
        <button class="btn-primary-purple-dark" @click="subscribe">
          Save {{ course.discountPercent }}%
        </button>
      </div>
    </div>
    <HeroSection :course="course" @subscribe="subscribe" />

    <div class="site-section">
      <div class="container">
        <div class="row">
          <div class="col col-10 offset-1">
            <div class="start">
              <h2 class="col-7">
                {{ course.startTitle }}
              </h2>
              <p class="body-1">
                {{ course.startDescription }}
              </p>
              <div class="stats">
                <div class="stat">
                  <div>{{ course.lessons }}</div>
                  <span class="h5">Lessons</span>
                </div>
                <div class="stat">
                  <div class="wrapper">
                    <div>{{ course.totalHours }}</div>
                    <span class="h5">Hours of video</span>
                  </div>
                </div>
                <div class="stat">
                  <div>{{ course.exercises }}</div>
                  <span class="h5">Practical tasks</span>
                </div>
              </div>
            </div>
            <div class="video">
              <VideoPlayer
                v-if="videoJSLoaded" slot="player" :video-id="course.videoId"
                class="video-player"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="site-section gray">
      <Curriculum
        :c="course"
      />
    </div>
    <div class="site-section dark">
      <Cards />
      <div class="container">
        <div class="row">
          <div class="col col-6 offset-3 learn">
            <h2>What You’ll Learn</h2>
            <p class="body-1">
              {{ learn.learn.replace(/\xA0/g,' ') }}
            </p>
            <ul v-if="learn.list">
              <template v-for="(l, idx) in learn.list">
                <li v-if="l" :key="idx" class="body-1">
                  <IconCheck /> {{ l }}
                </li>
              </template>
            </ul>
            <hr>
            <span>Our graduates work at</span>
            <div class="logos">
              <span class="logo">
                <LogoTesla />
              </span>
              <span class="logo">
                <LogoWalmart />
              </span>
              <span class="logo">
                <LogoCitibank />
              </span>
              <span class="logo">
                <LogoBooking />
              </span>
              <span class="logo">
                <LogoPaypal />
              </span>
            </div>
          </div>
          <div v-for="(instructor, idx) in course.instructorsArr" :key="idx" class="meet-author">
            <div class="col-5 col">
              <img loading="lazy" :src="`${$config.imagesUrl}/team/${instructor.image}`" :alt="instructor.name">
            </div>
            <div v-if="course.instructorsInfo[instructor.slug]" class="info col col-6 offset-1">
              <h2>Meet {{ course.instructorsArr[idx].name }}</h2>
              <p class="body-1" v-html="course.instructorsInfo[instructor.slug].instructorDescription[course.slug]" />
              <div class="stats">
                <div v-for="(s, i) in course.instructorsInfo[instructor.slug].stats" :key="i" class="stat">
                  <div>{{ s.stat }}</div>
                  <span v-html="s.description" />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="col-6 offset-3 buy-course">
          <h2>
            Buy Once. <br> Enjoy Lifetime Access.
          </h2>
          <div class="col-8 sm-6 btn-info">
            <button class="btn-primary btn-lg" @click="subscribe">
              Start Learning Now
            </button>
            <p>
              One-time payment. Lifetime access to all course materials and updates.
            </p>
          </div>
        </div>
      </div>
    </div>
    <div class="site-section gray">
      <Reviews
        :reviews-data="course.reviews"
        :title="course.slug === 'how-to-think-like-a-data-scientist-to-become-one' ? `${course.countStudents} Students Have Already Benefitted from This Course! ` : `${course.countStudents} Students Have Joined the Course!`"
        title-class="col col-6"
      />
      <FAQ />
    </div>
    <div class="limited-offer-holder">
      <LimitedOffer
        :options="limitedOfferOptions"
        :features="limitedOfferFeatures"
        @subscribe="subscribe"
      />
    </div>
  </div>
</template>

<script>
import global from '@itbpbg/lms-components/src/mixins/global';
import VideoPlayer from '@itbpbg/lms-components/src/components/VideoPlayer';
import moment from 'moment';
import CloseIcon from '@itbpbg/lms-components/src/assets/svg/close-icon.svg?vue-component';
import { countDownToDate } from '@itbpbg/lms-components/src/utils/helper';
import HeroSection from './HeroSection';
import Curriculum from './Curriculum';
import Cards from './Cards';
import FAQ from './FAQ';
import Reviews from '../Landing/Reviews';
import LimitedOffer from '../Landing/LimitedOffer';
import IconCheck from '../../assets/svg/icon-check.svg?vue-component';
import LogoTesla from '../../assets/svg/companies/logo-tesla.svg?vue-component';
import LogoWalmart from '../../assets/svg/companies/logo-walmart.svg?vue-component';
import LogoCitibank from '../../assets/svg/companies/logo-citibank.svg?vue-component';
import LogoBooking from '../../assets/svg/companies/logo-booking.svg?vue-component';
import LogoPaypal from '../../assets/svg/companies/logo-paypal.svg?vue-component';

export default {
  components: {
    VideoPlayer,
    HeroSection,
    Curriculum,
    Cards,
    FAQ,
    Reviews,
    LimitedOffer,
    IconCheck,
    LogoTesla,
    LogoWalmart,
    LogoCitibank,
    LogoBooking,
    LogoPaypal,
    CloseIcon,
  },
  mixins: [global],
  props: {
    seo: { type: Object, default: () => {}, required: true },
    courseInfo: { type: Object, default: () => {}, required: true },
  },
  data() {
    return {
      course: this.courseInfo,
      page: {},
      videoJSLoaded: false,
      limitedOfferFeatures: [
        {
          name: '',
        },
        {
          name: 'Content updates',
        },
        {
          name: 'Exam',
        },
        {
          name: this.courseInfo.slug !== 'product-management-for-ai-data-science' ? 'Learning resources' : 'Downloadable resources',
        },
        {
          name: 'Certificate of achievement',
        },
        {
          name: 'Resume builder',
        },
        {
          name: 'Self-paced learning',
        },
        {
          name: 'Lifetime access',
        },
      ],
      limitedOfferOptions: {
        title: this.courseInfo.limitedOfferOptionsTitle || 'Take Your Data Skills to the Next Level',
        regularPrice: '',
        discountedPrice: '',
        promoPlanButtonText: '',
        course: true,
      },
      countdownEndDate: this.saleEndDate(),
      banner: false,
      countdown: {
        days: 0,
        hours: 0,
        minutes: 0,
        seconds: 0,
      },
    };
  },
  head() {
    const metaTitle = `${this.page.metaTitle}`;

    const linkedData = this.$linkedDataLoad([
      { key: 'WebSite' },
      { key: 'WebPage', values: this.page },
      { key: 'Course', values: { name: metaTitle, description: this.page.metaDescription } },
    ]);

    const link = [];

    if (this.canonicalURL) {
      link.push({ rel: 'canonical', href: this.canonicalURL() });
    }

    return {
      titleTemplate: `${metaTitle}%s`,
      link,
      meta: this.metaProps({ ...this.page, metaImage: `og-${this.course.slug}.png` }),
      script: [
        { src: 'https://players.brightcove.net/6258000438001/FHDad0Z7G_default/index.min.js', async: true },
        linkedData,
      ],
    };
  },
  computed: {
    learn() {
      return JSON.parse(`${this.course.learn}`) || {};
    },
  },
  created() {
    this.page = this.seo;
    this.page.fullPath = `${this.$config.siteUrl}${this.$route.fullPath}`;
    this.page.created = this.page.created || new Date();
    this.page.updated = this.page.updated || new Date();
  },
  async mounted() {
    await this.$pollValue(() => typeof videojs !== 'undefined');
    this.videoJSLoaded = true;
    this.changeLimitedOfferProps();

    setTimeout(() => {
      const countDownDate = new Date(this.countdownEndDate);
      const x = setInterval(async () => {
        const interval = countDownToDate(new Date(), countDownDate);
        this.countdown = interval;
        if (!interval || !this.banner) {
          this.countdown = {
            days: '00',
            hours: '00',
            minutes: '00',
            seconds: '00',
          };
          clearInterval(x);
          // this.$emit('offer-expired');
          this.banner = false;
          if (this.$route.path === '/learn-machine-learning-process-a-z/') {
            await this.fetchCourse();
          }
        }
      }, 1000);
    }, 300);
  },
  methods: {
    async fetchCourse() {
      this.course = await this.$axios.$get(`/courses/landing${this.$route.path}`).catch(() => {});
      this.changeLimitedOfferProps();
    },
    subscribe() {
      return this.$router.push({ path: '/checkout/', query: { plan: this.course.plan, coupon: this.$route.query.coupon } });
    },
    saleEndDate() {
      const endDateUtc = moment.utc('December 08, 2022 08:00:00');
      const localCountDownDate = new Date(endDateUtc);
      return localCountDownDate;
    },
    changeLimitedOfferProps() {
      this.limitedOfferFeatures[0].name = `${this.course.lessons} Video Lessons`;
      this.limitedOfferOptions.regularPrice = `$${this.course.regularPrice}`;
      this.limitedOfferOptions.discountedPrice = `$${this.course.discountedPrice}`;
      this.limitedOfferOptions.promoPlanButtonText = this.course.discountPercent ? `Save ${this.course.discountPercent}% Now` : 'Get Now';
    },
  },
};
</script>

<style lang="scss">
.course-landing {
  overflow: hidden;

  .site-section {
    padding: var(--spacing-100) 0;

    .container {
      position: relative;
      z-index: 1;
    }

    &.gray {
      background-color: var(--main-light-shade-1);

      .faq-holder .questions .col-6 .qa-holder:last-of-type {
        margin-bottom: 0;
      }
    }

    &.dark {
      position: relative;
    }
  }

  .btn-primary {
    width: 100%;
  }

  .start {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    text-align: center;

    h2 {
      margin-bottom: var(--spacing-24);
    }

    .body-1 {
      margin: 0 0 var(--spacing-60);
    }

    .stats {
      display: flex;

      .stat {
        &:first-of-type {
          width: 148px;
          margin-right: 64px;
        }

        &:nth-of-type(2) {
          border-left: 1px solid var(--main-light-shade-4);
          border-right: 1px solid var(--main-light-shade-4);

          .wrapper {
            width: 148px;
            display: flex;
            flex-direction: column;
            margin: 0 64px;
          }
        }

        &:last-of-type {
          width: 148px;
          margin-left: 64px;
        }

        div {
          font-size: 57px;
          font-weight: 600;
          margin-bottom: var(--spacing-8);
        }

        span {
          color: var(--main-dark-shade-3);
        }
      }
    }
  }

  .video {
    margin-top: var(--spacing-100);
  }

  .learn {
    padding-top: var(--spacing-80);
    padding-bottom: var(--spacing-80);
    text-align: center;
    position: relative;

    h2 {
      margin: 0 0 11px;
    }

    p {
      margin: 0 0 var(--spacing-36);
    }

    ul {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      grid-column-gap: var(--spacing-30);
      list-style: none;
      margin: 0;
      padding: 0;

      li {
        text-align: left;
        display: flex;
        align-items: flex-start;
        padding-bottom: var(--spacing-26);

        &:not(:nth-of-type(5)):not(:nth-of-type(6)) {
          margin-bottom: var(--spacing-36);
        }

        svg {
          width: 20px;
          height: 20px;
          margin-right: var(--spacing-12);
          flex: 0 0 auto;

          path[fill] {
            fill: var(--accent);
          }
        }
      }
    }

    hr {
      border: none;
      height: 1px;
      background-color: rgba(255, 255, 255, 0.2);
      margin: var(--spacing-36) 0;
    }

    span {
      color: var(--main-light-shade-4);
    }

    .logos {
      display: flex;
      flex-wrap: wrap;
      justify-content: center;
      margin-top: var(--spacing-12);
      margin-left: -16px;
      margin-right: -16px;

      .logo {
        margin: var(--spacing-16);
        display: flex;
        align-items: center;
        justify-content: center;

        svg {
          width: 128px;

          path[fill="#fff"] {
            fill: var(--main-light-shade-4);
          }
        }
      }
    }
  }

  .meet-author {
    padding-top: var(--spacing-80);
    padding-bottom: var(--spacing-100);
    position: relative;

    img {
      width: 100%;
      height: 100%;
      border-radius: 10px;
    }

    .info {
      h2 {
        margin-bottom: var(--spacing-36);
      }

      .body-1 {
        &:first-of-type {
          margin-top: 0;
        }

        &:last-of-type {
          margin-bottom: 0;
        }
      }

      .stats {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-top: var(--spacing-36);

        .stat {
          display: flex;
          align-items: center;

          div {
            font-size: 57px;
            font-weight: 500;
            margin-right: var(--spacing-12);
          }

          span {
            font-size: 18px;
            color: var(--main-light-shade-4);
          }
        }
      }
    }
  }

  .buy-course {
    padding-top: var(--spacing-100);
    text-align: center;

    h2 {
      margin-bottom: var(--spacing-48);
    }

    .btn-info {
      margin: 0 auto;

      p {
        color: var(--main-light-shade-4);
      }
    }
  }

  .reviews {
    padding-bottom: var(--spacing-100);

    .reviews-trustpilot {
      .reviews-heading {
        > div {
          .heading.title {
            font-size: 40px;
            font-weight: 500;
          }
        }
      }
    }
  }

  .limited-offer-holder {
    padding: var(--spacing-100) 0;
    background-color: var(--main-dark);
    color: var(--main-light);
    position: relative;

    .container {
      z-index: 1;
      position: relative;
    }

    .heading {
      margin-bottom: 0;
    }

    hr {
      background-color: rgba(255, 255, 255, 0.24);
    }

    .plan-features {
      margin-bottom: 0;
    }

    button {
      max-width: 70%;
    }

    .vat-note {
      font-size: 16px;
      color: var(--main-light-shade-4);
    }
  }

  @include bp($bp-laptop) {
    .start .stats .stat div {
      font-size: 54px;
      line-height: 100%;
    }

    .reviews .reviews-trustpilot .reviews-heading > div .heading.title {
      font-size: 32px;
    }
  }

  @include bp($bp-mobile) {
    h2 {
      font-size: 32px;
    }

    .site-section {
      &:not(.gray):not(.dark) {
        padding-top: var(--spacing-100);
        padding-bottom: var(--spacing-100);
      }

      &.gray {
        padding: var(--spacing-100) 0;

        .curriculum {
          .container {
            .heading {
              padding-bottom: var(--spacing-4);
            }
          }
        }

        .reviews {
          .reviews-trustpilot {
            .reviews-heading {
              > div {
                .heading.title {
                  font-size: 28px;
                }
              }
            }
          }
        }
      }

      &.dark {
        padding-bottom: var(--spacing-80);

        &:before,
        &:after {
          display: none;
        }
      }
    }

    .start {
      .stats {
        .stat {
          &:first-of-type {
            width: 86px;
            margin-right: var(--spacing-24);
          }

          &:nth-of-type(2) {
            border-left: 1px solid var(--main-light-shade-4);
            border-right: 1px solid var(--main-light-shade-4);

            .wrapper {
              width: 86px;
              display: flex;
              flex-direction: column;
              margin: 0 var(--spacing-24);
            }
          }

          &:last-of-type {
            width: 86px;
            margin-left: var(--spacing-24);
          }

          div {
            font-size: 36px;
            margin-bottom: var(--spacing-8);
          }

          span {
            font-size: 18px;
          }
        }
      }
    }

    .video {
      padding-top: var(--spacing-60);

      .player.video-player.video-js {
        border-radius: 10px;
      }
    }

    .learn {
      padding-top: var(--spacing-40);
      padding-bottom: var(--spacing-40);

      &:before {
        display: none;
      }

      h2,
      .body-1 {
        text-align: left;
      }

      ul {
        grid-template-columns: 1fr;
        padding: 0;

        li:nth-of-type(5) {
          margin-bottom: var(--spacing-36);
        }
      }
    }

    .meet-author {
      flex-direction: column;
      padding-bottom: var(--spacing-40);

      &:before {
        display: none;
      }

      img {
        margin: 0 0 var(--spacing-36);
        width: 100%;
        height: 100%;
        max-width: 500px;
      }

      .info {
        h2 {
          margin-bottom: var(--spacing-16);
        }

        .stats {
          flex-direction: column;
          align-items: flex-start;

          .stat:first-of-type {
            margin-bottom: var(--spacing-36);
          }
        }
      }
    }

    .buy-course {
      padding-top: var(--spacing-80);
    }

    .reviews-trustpilot {
      .reviews-heading {
        > div {
          .heading.title {
            font-size: 28px;
          }
        }
      }
    }

    .limited-offer {
      &:before,
      &:after {
        display: none;
      }

      .price {
        .discounted-price {
          font-size: 40px;
        }

        .regular-price {
          font-size: 24px;
        }
      }

      .plan-features {
        grid-column-gap: var(--spacing-36);
        grid-row-gap: var(--spacing-16);

        .feature {
          font-size: 14px;
        }
      }
    }
  }

  @include bp($bp-phone) {
    h2 {
      font-size: 28px;
    }

    .site-section {
      &.dark {
        padding-top: var(--spacing-80);

        .cards-heading {
          margin-bottom: var(--spacing-36);
        }

        .cards {
          .card {
            margin: 0 0 var(--spacing-16);
          }
        }
      }

      &.gray {
        padding-bottom: var(--spacing-80);

        .reviews {
          padding-bottom: var(--spacing-30);

          .reviews-trustpilot .reviews-heading > div {
            margin-bottom: var(--spacing-36);
          }
        }

        .faq-holder {
          padding-top: var(--spacing-80);
        }
      }
    }

    .module-holder {
      .module .module-body .body-2 {
        margin-bottom: var(--spacing-16);
      }
    }

    .learn {
      padding-top: var(--spacing-80);
      padding-bottom: var(--spacing-80);
    }

    .limited-offer {
      .plan-features {
        .body-2 {
          margin-bottom: var(--spacing-12);
        }
      }

      .btn-lg {
        max-width: 100%;
        margin-top: var(--spacing-20);
      }
    }

    .buy-course {
      .btn-info p {
        margin-bottom: 0;
      }

      h2 {
        margin-bottom: var(--spacing-40);
      }
    }
  }
}
</style>
